<template>
  <BlBackdrop
    title="Duplicate draft from past session"
    :footer="false"
    @backdrop-close="$emit('close-modal')"
  >
    <div class="flex flex-col gap-4 px-8 pb-8 w-200">
      <BlTextBody2
        text="Select any of your last 50 sessions to automatically add all its targets to your current draft."
      />
      <div class="flex">
        <div
          class="relative pr-5 overflow-y-auto w-100"
          :style="{ height: 'calc(100vh - 20rem)' }"
        >
          <div class="sticky top-0 z-10 pb-3 bg-pure-white">
            <BlInputSearch :input="searchInput" @changed="onInputSearch" />
          </div>

          <div v-if="fetchLoading" class="grid my-32 place-items-center">
            <div class="blum-loader-sm"></div>
          </div>
          <div
            v-else
            v-for="session in sessions"
            :key="session.id"
            class="flex gap-2 px-3 py-2 border-b cursor-pointer border-slate-400"
            @click="onSelectSession(session)"
          >
            <BlInputRadio
              :name="`session_${session.id}`"
              :checked="selectedSessionId === session.id"
            />
            <div class="flex flex-col gap-1">
              <BlTextBody2 :text="session.name || 'Unnamed session'" />
              <div class="flex items-center gap-1">
                <BlTextBody3 :text="`ID ${session.id}`" />
                <div
                  class="flex-shrink-0 w-1 h-1 rounded-full bg-slate-300"
                ></div>
                <BlTextBody3
                  :text="`${session.number_of_measurements} target(s)`"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          class="px-5 pb-3 overflow-y-auto w-100 bg-slate-200"
          :style="{ height: 'calc(100vh - 20rem)' }"
        >
          <div v-if="!selectedSessionId" class="flex justify-center my-32">
            <BlTextBody2 text="Select a session to preview the targets" />
          </div>
          <div v-else>
            <div class="sticky top-0 z-10 py-3 bg-slate-200">
              <BlTextBody2
                :text="
                  `Preview ${selectedSessionData.number_of_measurements} target(s)`
                "
              />
            </div>
            <div v-if="measurementLoading" class="flex justify-center my-32">
              <div class="blum-loader-sm"></div>
            </div>
            <div
              v-else-if="!measurements.length"
              class="flex justify-center my-32"
            >
              <BlTextBody2 text="Target(s) not found." />
            </div>
            <div class="flex flex-col">
              <TargetCard
                v-for="measurement in measurements"
                :key="measurement.id"
                :class="{ 'my-2' : measurement.target.target_tasks && measurement.target.target_tasks.length > 0 }"
                :isFromSession="true"
                :target="measurement.target"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-4">
        <BlInputButton
          type="plain"
          label="Cancel"
          @clicked="$emit('close-modal')"
        />
        <BlInputButton
          :label="`Use Session ID ${selectedSessionId}`"
          :disabled="!selectedSessionId || (selectedSessionData && selectedSessionData.number_of_measurements === 0) "
          :loading="submitLoading"
          @clicked="onDuplicate"
        />
      </div>
    </div>
  </BlBackdrop>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import debounce from "lodash/debounce";

import TargetCard from "../../clients/target/Card.vue";

import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlInputSearch from "../../BlInputSearch.vue";
import BlInputRadio from "../../BlInputRadio.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "DuplicateSessionModal",
  components: {
    TargetCard,
    BlTextBody2,
    BlTextBody3,
    BlInputSearch,
    BlInputRadio,
    BlInputButton,
    BlBackdrop
  },
  data() {
    return {
      fetchLoading: false,
      submitLoading: false,
      searchInput: {
        model: "",
        placeholder: "Search by name or ID"
      },
      sessionCount: 0,
      selectedSessionId: "",
      selectedSessionData: null,
      measurementLoading: false,
      measurements: []
    };
  },
  computed: {
    ...mapGetters(["client", "session", "sessions"])
  },
  mounted() {
    this.fetchSessions();
  },
  methods: {
    ...mapActions([
      "getSessions",
      "getMeasurements",
      "duplicateTargetsSession"
    ]),
    onInputSearch(payload) {
      payload.input.model = payload.value;
      this.onSearching();
    },
    onSearching: debounce(function() {
      this.fetchSessions();
    }, 1000),
    fetchSessions() {
      let params = `?page=1&per_page=50&exclude_mastered=true&session_id=${this.session.id}`;
      if (this.searchInput.model) params += `&query=${this.searchInput.model}`;
      this.fetchLoading = true;
      this.getSessions({ id: this.client.id, params }).then(
        ({ success, data }) => {
          this.fetchLoading = false;
          if (!success) return;
          this.sessionCount = data.total_count;
        }
      );
    },
    async onSelectSession(val) {
      this.selectedSessionId = val.id;
      this.selectedSessionData = val;
      this.measurements = [];

      this.measurementLoading = true;
      const { success, data } = await this.getMeasurements({
        id: val.id,
        skip_action: true,
        params: "?exclude_mastered=true"
      });
      this.measurementLoading = false;
      if (!success) return;
      this.measurements = data;
    },
    onDuplicate() {
      const data = {
        id: this.session.id,
        selected_session_id: this.selectedSessionId
      };
      this.submitLoading = true;
      this.duplicateTargetsSession(data).then(({ success }) => {
        this.submitLoading = false;
        if (!success) return;
        this.$emit("close-modal");
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/sessions/draft/DuplicateSessionModal.vue